/**
 * New Assessment V2 Page
 *
 * Phase 8.1: 4-step wizard for creating question-based assessments:
 * 1. Selection - Choose operator and SOP
 * 2. Job Selection - Choose which job (section) to assess
 * 3. Questions - Score 10-13 questions using 6-point rubric
 * 4. Review - Review category scores and submit
 */

import { useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ClipboardCheck,
  User,
  Briefcase,
  FileQuestion,
  CheckCircle2,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { useSop } from '../../hooks/useSOPs';
import {
  useRubricOptions,
  useSectionQuestions,
  useCreateAssessmentV2,
} from '../../hooks/useAssessments';
import { SelectionStep } from './components/SelectionStep';
import { JobSelectionStep } from './components/JobSelectionStep';
import { QuestionsStep } from './components/QuestionsStep';
import { ReviewStepV2 } from './components/ReviewStepV2';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';

// ============================================
// TYPES
// ============================================

interface QuestionResponse {
  score: number | null;
  feedback: string;
}

interface AssessmentV2FormData {
  // Step 1: Selection
  operatorId: string;
  sopId: string;

  // Step 2: Job Selection
  sectionId: string;

  // Step 3: Question Responses (keyed by questionId)
  questionResponses: Record<string, QuestionResponse>;

  // Step 4: Review
  managerNotes: string;
}

type WizardStep = 1 | 2 | 3 | 4;

// ============================================
// INITIAL STATE
// ============================================

const initialFormData: AssessmentV2FormData = {
  operatorId: '',
  sopId: '',
  sectionId: '',
  questionResponses: {},
  managerNotes: '',
};

// ============================================
// MAIN COMPONENT
// ============================================

export function NewAssessmentV2Page() {
  useDocumentTitle('New Assessment');
  const navigate = useNavigate();

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [formData, setFormData] = useState<AssessmentV2FormData>(initialFormData);
  const [error, setError] = useState<string | null>(null);

  // Data fetching
  const { data: sop, isLoading: sopLoading, refetch: refetchSop } = useSop(formData.sopId || null);
  const { data: rubricOptions = [], isLoading: rubricLoading } = useRubricOptions();
  const {
    data: sectionData,
    isLoading: questionsLoading,
    isError: questionsError,
    error: questionsErrorDetails,
    refetch: refetchQuestions,
  } = useSectionQuestions(
    formData.sopId || null,
    formData.sectionId || null
  );

  // Create assessment mutation
  const createAssessment = useCreateAssessmentV2();

  // Get selected section info
  const selectedSection = useMemo(() => {
    if (!sop?.sections || !formData.sectionId) return null;
    return sop.sections.find((s) => s.id === formData.sectionId);
  }, [sop?.sections, formData.sectionId]);

  // ============================================
  // EVENT HANDLERS
  // ============================================

  const handleSelectionChange = useCallback((operatorId: string, sopId: string) => {
    setFormData((prev) => ({
      ...prev,
      operatorId,
      sopId,
      // Reset downstream selections when SOP changes
      sectionId: prev.sopId !== sopId ? '' : prev.sectionId,
      questionResponses: prev.sopId !== sopId ? {} : prev.questionResponses,
    }));
    setError(null);
  }, []);

  const handleSectionSelect = useCallback((sectionId: string) => {
    setFormData((prev) => ({
      ...prev,
      sectionId,
      // Reset question responses when section changes
      questionResponses: prev.sectionId !== sectionId ? {} : prev.questionResponses,
    }));
    setError(null);
  }, []);

  const handleResponseChange = useCallback((questionId: string, response: QuestionResponse) => {
    setFormData((prev) => ({
      ...prev,
      questionResponses: {
        ...prev.questionResponses,
        [questionId]: response,
      },
    }));
  }, []);

  const handleNotesChange = useCallback((notes: string) => {
    setFormData((prev) => ({ ...prev, managerNotes: notes }));
  }, []);

  // ============================================
  // NAVIGATION
  // ============================================

  const canProceedToStep2 = formData.operatorId && formData.sopId && !sopLoading;
  const canProceedToStep3 = formData.sectionId && (selectedSection?.questionCount ?? 0) > 0;
  const answeredCount = Object.values(formData.questionResponses).filter(
    (r) => r.score !== null
  ).length;
  const canProceedToStep4 = answeredCount > 0;

  const handleNext = async () => {
    if (currentStep === 1 && canProceedToStep2) {
      // Refetch SOP to ensure we have the latest section IDs
      // (sections may have been recreated during reprocessing)
      await refetchSop();
      setCurrentStep(2);
    } else if (currentStep === 2 && canProceedToStep3) {
      setCurrentStep(3);
    } else if (currentStep === 3 && canProceedToStep4) {
      setCurrentStep(4);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => (prev - 1) as WizardStep);
    }
  };

  const handleSubmit = async () => {
    setError(null);

    // Validate
    if (!formData.operatorId || !formData.sopId || !formData.sectionId) {
      setError('Please complete all required selections');
      return;
    }

    const responses = Object.entries(formData.questionResponses)
      .filter(([, r]) => r.score !== null)
      .map(([questionId, r]) => ({
        questionId,
        rubricScore: r.score as 0 | 20 | 40 | 60 | 80 | 100,
        feedback: r.feedback || undefined,
      }));

    if (responses.length === 0) {
      setError('Please score at least one question');
      return;
    }

    try {
      await createAssessment.mutateAsync({
        operatorId: formData.operatorId,
        sopId: formData.sopId,
        sectionId: formData.sectionId,
        managerNotes: formData.managerNotes || undefined,
        questionResponses: responses,
      });

      // Navigate to assessments list on success
      navigate('/assessments');
    } catch (err) {
      console.error('Create assessment error:', err);
      setError(err instanceof Error ? err.message : 'Failed to create assessment');
    }
  };

  // ============================================
  // RENDER
  // ============================================

  const stepLabels = ['Select', 'Job', 'Questions', 'Review'];
  const stepIcons = [User, Briefcase, FileQuestion, CheckCircle2];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Subtle background pattern */}
      <div className="fixed inset-0 opacity-[0.015] pointer-events-none">
        <svg width="100%" height="100%">
          <defs>
            <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M 32 0 L 0 0 0 32" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="relative max-w-5xl mx-auto px-6 py-8">
        {/* Back navigation */}
        <Link
          to="/assessments"
          className="inline-flex items-center gap-2 text-gray-500 hover:text-[#00674a] transition-colors mb-8 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Back to Assessments</span>
        </Link>

        {/* Header */}
        <div className="flex items-center gap-4 mb-10">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#00674a] to-[#00674a]/80 flex items-center justify-center shadow-xl shadow-[#00674a]/20">
            <ClipboardCheck className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
              New Assessment
            </h1>
            <p className="text-gray-500 mt-1">
              Create a question-based operator assessment
            </p>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8">
          <div className="flex items-center justify-center gap-2 sm:gap-4">
            {[1, 2, 3, 4].map((step, index) => {
              const StepIcon = stepIcons[index];
              const isActive = currentStep === step;
              const isCompleted = currentStep > step;

              return (
                <div key={step} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div
                      className={`
                        relative w-12 h-12 rounded-xl flex items-center justify-center
                        transition-all duration-500 ease-out
                        ${
                          isActive
                            ? 'bg-[#00674a] text-white scale-110 shadow-lg shadow-[#00674a]/30'
                            : isCompleted
                            ? 'bg-[#c5e8c2] text-[#00674a]'
                            : 'bg-gray-100 text-gray-400'
                        }
                      `}
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="w-6 h-6" />
                      ) : (
                        <StepIcon className="w-5 h-5" />
                      )}
                      {isActive && (
                        <div className="absolute inset-0 rounded-xl ring-4 ring-[#00674a]/20 animate-pulse" />
                      )}
                    </div>
                    <span
                      className={`
                        mt-2 text-xs font-semibold tracking-wide uppercase
                        ${
                          isActive
                            ? 'text-[#00674a]'
                            : isCompleted
                            ? 'text-[#00674a]/70'
                            : 'text-gray-400'
                        }
                      `}
                    >
                      {stepLabels[index]}
                    </span>
                  </div>
                  {step < 4 && (
                    <div
                      className={`
                        w-8 sm:w-16 h-1 mx-2 sm:mx-3 rounded-full transition-colors duration-500
                        ${currentStep > step ? 'bg-[#c5e8c2]' : 'bg-gray-200'}
                      `}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Error display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Content Area */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Step 1: Selection */}
          {currentStep === 1 && (
            <SelectionStep
              operatorId={formData.operatorId}
              sopId={formData.sopId}
              sop={sop}
              sopLoading={sopLoading}
              onChange={handleSelectionChange}
            />
          )}

          {/* Step 2: Job Selection */}
          {currentStep === 2 && sop && (
            <JobSelectionStep
              sections={sop.sections ?? []}
              selectedSectionId={formData.sectionId}
              onSelect={handleSectionSelect}
              sopTitle={sop.title}
            />
          )}

          {/* Step 3: Questions */}
          {currentStep === 3 && (
            questionsError ? (
              <div className="p-8 text-center">
                <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Failed to load questions
                </h3>
                <p className="text-gray-500 max-w-md mx-auto mb-6">
                  {questionsErrorDetails instanceof Error
                    ? questionsErrorDetails.message
                    : 'Unable to load assessment questions. The job may have been modified.'}
                </p>
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => refetchQuestions()}
                    className="px-6 py-2.5 bg-[#00674a] text-white font-semibold rounded-xl hover:bg-[#005540] transition-colors"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={handleBack}
                    className="px-6 py-2.5 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Go Back
                  </button>
                </div>
              </div>
            ) : (
              <QuestionsStep
                questions={sectionData?.questions ?? []}
                rubricOptions={rubricOptions}
                responses={formData.questionResponses}
                onResponseChange={handleResponseChange}
                sectionTitle={selectedSection?.title}
                isLoading={questionsLoading || rubricLoading}
              />
            )
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <ReviewStepV2
              sopTitle={sop?.title ?? ''}
              sectionTitle={selectedSection?.title ?? ''}
              questions={sectionData?.questions ?? []}
              rubricOptions={rubricOptions}
              responses={formData.questionResponses}
              managerNotes={formData.managerNotes}
              onNotesChange={handleNotesChange}
            />
          )}

          {/* Footer */}
          <div className="px-8 py-6 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between gap-4">
            <div>
              {currentStep > 1 && (
                <button
                  onClick={handleBack}
                  className="px-6 py-3 text-gray-600 font-semibold hover:text-gray-900 transition-colors"
                >
                  Back
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              {currentStep === 3 && sectionData?.questions && (
                <span className="text-sm text-gray-500">
                  <span className="font-semibold text-gray-700">{answeredCount}</span> of{' '}
                  <span className="font-semibold text-gray-700">
                    {sectionData.questions.length}
                  </span>{' '}
                  questions scored
                </span>
              )}

              {currentStep < 4 ? (
                <button
                  onClick={handleNext}
                  disabled={
                    (currentStep === 1 && !canProceedToStep2) ||
                    (currentStep === 2 && !canProceedToStep3) ||
                    (currentStep === 3 && !canProceedToStep4)
                  }
                  className="px-8 py-3 bg-[#00674a] text-white font-semibold rounded-xl hover:bg-[#005540] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-lg shadow-[#00674a]/20"
                >
                  Continue
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={createAssessment.isPending}
                  className="px-8 py-3 bg-[#00674a] text-white font-semibold rounded-xl hover:bg-[#005540] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-lg shadow-[#00674a]/20"
                >
                  {createAssessment.isPending ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      Submit Assessment
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewAssessmentV2Page;
